// File: src/layouts/AppLayout.tsx
import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { LayoutProvider } from '../contexts/LayoutContext';
import CommonHeader from './CommonHeader';
import Sidebar from './Sidebar';
import { useAuth } from '../hooks/useAuth';
import { useDirection } from '../hooks/useDirection';
import { CoachProfile, UserRoleEnum, WeeklySummaryCreatePayload, WellBeingUpdatePayload } from '../types';
import WeeklySummaryModal from '../features/daily-planner/components/WeeklySummaryModal';
import plannerService from '../services/plannerService';
import { toast } from 'react-hot-toast';
import { subDays, startOfWeek, format } from 'date-fns';
import Modal from '../components/common/Modal';
import WellBeingForm from '../features/daily-planner/components/WellBeingForm';
import { UserProfileProvider } from '../contexts/UserContext';
import { useUserProfile } from '../hooks/useUserProfile';
import CoachOnboardingModal from '../features/coach/components/CoachOnboardingModal';
import Button from '../components/common/Button';
import { CpuChipIcon } from '@heroicons/react/24/outline';

const OnboardingWrapper: React.FC = () => {
    const { user, isLoading: isAuthLoading } = useAuth();
    const { profile, isLoadingProfile } = useUserProfile();
    const [isCoachOnboardingModalOpen, setIsCoachOnboardingModalOpen] = useState(false);

    useEffect(() => {
        if (isAuthLoading || isLoadingProfile) return;

        if (user?.role_name === UserRoleEnum.Coach && profile) {
            const coachProfile = profile as CoachProfile;
            const hasCompletedOnboarding = !!coachProfile.city;
            const hasDismissedOnboarding = sessionStorage.getItem('coachOnboardingDismissed');

            if (!hasCompletedOnboarding && !hasDismissedOnboarding) {
                setIsCoachOnboardingModalOpen(true);
            }
        }
    }, [user, profile, isAuthLoading, isLoadingProfile]);

    const handleCloseOnboarding = () => {
        sessionStorage.setItem('coachOnboardingDismissed', 'true');
        setIsCoachOnboardingModalOpen(false);
    };

    return (
        <>
            <Outlet />
            <CoachOnboardingModal
                isOpen={isCoachOnboardingModalOpen}
                onClose={handleCloseOnboarding}
            />
        </>
    );
};

const AppLayout: React.FC = () => {
  const { user, isLoading: isAuthLoading } = useAuth();
  const { isRTL } = useDirection();
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [isSubmittingSummary, setIsSubmittingSummary] = useState(false);
  const [summaryWeekStart, setSummaryWeekStart] = useState<Date | null>(null);

  const [isWellBeingModalOpen, setIsWellBeingModalOpen] = useState(false);
  const [isSubmittingWellBeing, setIsSubmittingWellBeing] = useState(false);
  
  const [isRechargeModalOpen, setIsRechargeModalOpen] = useState(false);
  const navigate = useNavigate();

  // REMOVED: The notification handling useEffect has been removed from here
  // as its logic is now centralized in the new NotificationRedirectPage.

  useEffect(() => {
    if (isAuthLoading || !user) return;

    if (user.role_name === UserRoleEnum.Client) {
        const todayString = format(new Date(), 'yyyy-MM-dd');
        const wellBeingCheckKey = `lastWellBeingPrompt_${todayString}`;
        if (!sessionStorage.getItem(wellBeingCheckKey)) {
            plannerService.getWellBeingByDate(todayString)
                .then(existingEntry => {
                    sessionStorage.setItem(wellBeingCheckKey, 'true');
                    if (!existingEntry) setIsWellBeingModalOpen(true);
                })
                .catch(error => {
                    console.error("[WellBeingCheck] Error fetching well-being entry:", error);
                    sessionStorage.setItem(wellBeingCheckKey, 'true');
                });
        }
        const today = new Date();
        const lastWeekStartDate = startOfWeek(subDays(today, 7), { weekStartsOn: 1 });
        const lastWeekStartDateString = format(lastWeekStartDate, 'yyyy-MM-dd');
        const summaryCheckKey = `dismissed_summary_prompt_${lastWeekStartDateString}`;
        const currentDay = today.getDay(); 
        if ([0, 1, 2].includes(currentDay)) {
            if (!localStorage.getItem(summaryCheckKey)) {
                plannerService.getWeeklySummaryByDate(lastWeekStartDateString)
                    .then(existingSummary => {
                        if (!existingSummary) {
                            setSummaryWeekStart(lastWeekStartDate);
                            setShowSummaryModal(true);
                        }
                    })
                    .catch(error => console.error("[SummaryCheck] Error fetching weekly reflections:", error));
            }
        }
    } else if (user.role_name === UserRoleEnum.Coach) {
        const balance = user.ai_credit_balance ? parseFloat(user.ai_credit_balance) : 0;
        const rechargePromptKey = `rechargePromptDismissed_${user.id}`;

        if (balance < 0 && !sessionStorage.getItem(rechargePromptKey)) {
            setIsRechargeModalOpen(true);
        }
    }
  }, [user, isAuthLoading]);

  const handleRechargeModalClose = (dismiss: boolean) => {
    if (dismiss && user) {
        sessionStorage.setItem(`rechargePromptDismissed_${user.id}`, 'true');
    }
    setIsRechargeModalOpen(false);
  };

  const handleGoToRecharge = () => {
    handleRechargeModalClose(false);
    navigate('/app/coach/ai-credits');
  };

  const handleSummarySubmit = async (data: WeeklySummaryCreatePayload) => {
    if (!summaryWeekStart) return;
    setIsSubmittingSummary(true);
    try {
        await plannerService.upsertWeeklySummary(format(summaryWeekStart, 'yyyy-MM-dd'), data);
        toast.success("Thank you for your reflection!");
        handleCloseSummaryModal();
    } catch (error) {
        toast.error("Could not save your summary. Please try again.");
    } finally {
        setIsSubmittingSummary(false);
    }
  };
  
  const handleCloseSummaryModal = () => {
    if (summaryWeekStart) {
        const checkKey = `dismissed_summary_prompt_${format(summaryWeekStart, 'yyyy-MM-dd')}`;
        localStorage.setItem(checkKey, 'true');
    }
    setShowSummaryModal(false);
  };
  
  const handleWellBeingSubmit = async (data: WellBeingUpdatePayload) => {
    setIsSubmittingWellBeing(true);
    const todayString = format(new Date(), 'yyyy-MM-dd');
    try {
        await plannerService.upsertWellBeingEntry(todayString, data);
        toast.success("Today's check-in saved!");
        setIsWellBeingModalOpen(false);
    } catch (error) {
        toast.error("Could not save your check-in.");
    } finally {
        setIsSubmittingWellBeing(false);
    }
  };

  const handleCloseWellBeingModal = () => {
    setIsWellBeingModalOpen(false);
  };

  return (
    <LayoutProvider>
      <UserProfileProvider>
        <div className="min-h-screen flex flex-col">
          <CommonHeader />
          <div className={`flex flex-1 overflow-hidden ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Sidebar />
            <main className="flex-1 p-4 sm:p-6 lg:p-8 bg-neutral-light overflow-y-auto">
              <OnboardingWrapper />
            </main>
          </div>
        </div>
      </UserProfileProvider>

      {isWellBeingModalOpen && (
        <Modal
            isOpen={isWellBeingModalOpen}
            onClose={handleCloseWellBeingModal}
            title="Daily Well-being Check-in"
            size="md"
        >
            <WellBeingForm
                onSubmit={handleWellBeingSubmit}
                isLoading={isSubmittingWellBeing}
                onCancel={handleCloseWellBeingModal}
                currentDate={format(new Date(), 'yyyy-MM-dd')}
            />
        </Modal>
      )}

      {summaryWeekStart && (
         <WeeklySummaryModal
            isOpen={showSummaryModal}
            onClose={handleCloseSummaryModal}
            onSubmit={handleSummarySubmit}
            isLoading={isSubmittingSummary}
            weekDisplayName={format(summaryWeekStart, 'MMMM d')}
        />
      )}

      <Modal 
        isOpen={isRechargeModalOpen} 
        onClose={() => handleRechargeModalClose(true)}
        title="AI Credit Balance Low"
      >
        <div className="text-center">
            <p className="text-neutral-dark mb-4">Your AI credit balance is negative. Please purchase more credits to continue using AI-powered features.</p>
            <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={() => handleRechargeModalClose(true)}>Dismiss</Button>
                <Button variant="primary" onClick={handleGoToRecharge}>
                    <CpuChipIcon className="h-5 w-5 mr-2" />
                    Go to Recharge
                </Button>
            </div>
        </div>
      </Modal>
    </LayoutProvider>
  );
};

export default AppLayout;